// redux/supportChat.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../helper/Api';

const initialState = {
  users: [], // List of users on the left side
  activeUser: null, // The user currently chatting with on the right side
  messages: [],
  students: [],
  activeUsers: [],
  isLoading: false,
  isChatloading: false,
  assignedAdmin: null // NEW: Track assigned admin for student
};

// Support Chat History
export const getSupportUsersChat = createAsyncThunk('support/get-history', async (body, { rejectWithValue, dispatch }) => {
  try {
    console.log("🔄 Calling SUPPORT chat history API with search:", body);
    dispatch(setSupportLoading(true));
    const { data, status } = await api.getSupportChatHistory(body);
    console.log("✅ SUPPORT chat history response:", data);
    
    if (status === 200) {
      dispatch(setSupportUsers(data.data));
    }
  } catch (err) {
    console.error("❌ SUPPORT chat history error:", err);
    dispatch(setSupportLoading(false));
    return rejectWithValue(err.response?.data?.message || 'Something went wrong. Please try again later.');
  } finally {
    dispatch(setSupportLoading(false));
  }
});

// Get Support Chats
export const getAllSupportChats = createAsyncThunk('support/get-chats', async (body, { rejectWithValue, dispatch }) => {
  try {
    console.log("🔄 Calling SUPPORT get chats API for recipient:", body);
    dispatch(setSupportChatLoading(true));
    const { data, status } = await api.getSupportChats(body);
    console.log("✅ SUPPORT get chats response:", data);
    
    if (status === 200) {
      dispatch(setSupportMessages(data.data));
    }
  } catch (err) {
    console.error("❌ SUPPORT get chats error:", err);
    return rejectWithValue(err.response?.data?.message || 'Something went wrong. Please try again later.');
  } finally {
    dispatch(setSupportChatLoading(false));
  }
});

// Get Active Admins for Support
export const getAllActiveAdminsForSupport = createAsyncThunk('support/get-admins', async (body, { rejectWithValue, dispatch }) => {
  try {
    console.log("🔄 Fetching support admins");
    const { data, status } = await api.getActiveAdminsForSupport(body);

    if (status === 200) {
      dispatch(setSupportActiveUsers(data.data));
    }
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Something went wrong. Please try again later.');
  }
});

// Get Active Students for Support
export const getAllActiveStudentsForSupport = createAsyncThunk('support/get-students', async (body, { rejectWithValue, dispatch }) => {
  try {
    const { data, status } = await api.getActiveStudentsForSupport();

    if (status === 200) {
      dispatch(setSupportStudents(data.data));
    }
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Something went wrong. Please try again later.');
  }
});

// Create Support Message (Updated for student auto-assignment)
export const createSupportMessage = createAsyncThunk('support/add-message', async (body, { rejectWithValue, dispatch }) => {
  try {
    console.log("🔄 Creating SUPPORT message:", body);
    
    // For students, remove recipientId to trigger auto-assignment
    const messageData = { ...body };
    if (body.senderType === 'student') {
      delete messageData.recipientId;
    }
    
    const { data, status } = await api.addSupportMessage(messageData);

    if (status === 200) {
      dispatch(getSupportUsersChat(""));
      
      // If this is a student and we got assignment info, update state
      if (data.data?.assignedTo) {
        dispatch(setAssignedAdmin(data.data.assignedTo));
      }
    }
    return data;
  } catch (err) {
    console.error("❌ Support message creation failed:", err);
    return rejectWithValue(err.response?.data?.message || 'Something went wrong. Please try again later.');
  }
});



// NEW: Get assignment status for student
// export const getStudentAssignmentStatus = createAsyncThunk('support/get-student-assignment', async (body, { rejectWithValue, dispatch }) => {
//   try {
//     console.log("🔄 Getting student assignment status");
//     const { data, status } = await api.getChatAssignmentStatus(body);

//     if (status === 200) {
//       if (data.data?.isAssigned) {
//         dispatch(setAssignedAdmin(data.data.assignedTo));
//       }
//       return data;
//     }
//   } catch (err) {
//     console.error("❌ Failed to get student assignment status:", err);
//     return rejectWithValue(err.response?.data?.message || 'Something went wrong. Please try again later.');
//   }
// });

const supportChatSlice = createSlice({
  name: 'supportChat',
  initialState,
  reducers: {
    setSupportUsers: (state, action) => {
      state.users = action.payload;
    },
    setSupportActiveUsers: (state, action) => {
      state.activeUsers = action.payload;
    },
    setSupportMessages: (state, action) => {
      state.messages = action.payload;
    },
    setSupportLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setSupportChatLoading: (state, action) => {
      state.isChatloading = action.payload;
    },
    setSupportActiveUser: (state, action) => {
      state.activeUser = action.payload;
    },
    setSupportStudents: (state, action) => {
      state.students = action.payload;
    },
    addSupportMessage: (state, action) => {
      const { userId, message } = action.payload;
      if (!state.messages[userId]) {
        state.messages[userId] = [];
      }
      state.messages[userId].push(message);
    },
    updateSupportChatHistories: (state, action) => {
      const { userId, lastMessage, lastMessageTime, read } = action.payload;
    
      const userIndex = state.users.findIndex((user) => user.userId === userId);
    
      if (userIndex !== -1) {
        state.users[userIndex].lastMessage = lastMessage;
        state.users[userIndex].lastMessageTime = lastMessageTime;
        state.users[userIndex].read = read;
        
        const updatedUser = state.users.splice(userIndex, 1)[0];
        state.users.unshift(updatedUser);
      }
    },
    addNewSupportChatHistory: (state, action) => {
      state.users = [action.payload, ...state.users];
    },
    addSupportClientMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    updateSupportReadStatus: (state, action) => {
      const { userId, read } = action.payload;
      const userIndex = state.users.findIndex((user) => user.userId === userId);

      if (userIndex !== -1) {
        state.users[userIndex].read = read;
      }
    },
    // NEW: Student assignment reducers
    setAssignedAdmin: (state, action) => {
      state.assignedAdmin = action.payload;
    },
    clearAssignedAdmin: (state) => {
      state.assignedAdmin = null;
    },
    // NEW: Handle assignment from socket
    handleAdminAssignment: (state, action) => {
      const { adminId, adminName } = action.payload;
      state.assignedAdmin = adminId;
      
      // Update active user if it's the support user
      if (state.activeUser && state.activeUser.userId === "support") {
        state.activeUser = {
          ...state.activeUser,
          userId: adminId,
          userName: adminName || "Support Agent"
        };
      }
    }
  },
});

export const { 
  setSupportUsers, 
  setSupportActiveUser, 
  addSupportMessage, 
  updateSupportChatHistories, 
  setSupportLoading, 
  setSupportChatLoading, 
  setSupportMessages, 
  addNewSupportChatHistory, 
  addSupportClientMessage, 
  setSupportStudents, 
  setSupportActiveUsers, 
  updateSupportReadStatus,
  // NEW: Export student assignment reducers
  setAssignedAdmin,
  clearAssignedAdmin,
  handleAdminAssignment
} = supportChatSlice.actions;

export default supportChatSlice.reducer;